import { useCallback, useState } from 'react';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { useUpdateDriverGroup } from '@/modules/driver-groups/hooks/useDriverGroups';
import { toggleMemberId } from '@/modules/driver-groups/effects/toggleMemberId';
import { isDriverGroupDraftDirty } from '@/modules/driver-groups/effects/isDriverGroupDraftDirty';
import { saveDriverGroupDraft } from '@/modules/driver-groups/effects/saveDriverGroupDraft';

interface DriverGroupEditor {
  activeGroupId: string | null;
  activeGroup: DriverGroupDto | null;
  isEditing: boolean;
  draftName: string;
  draftMemberIds: Set<string>;
  isDirty: boolean;
  isNameValid: boolean;
  isSaving: boolean;
  saveError: Error | null;
  selectGroup: (groupId: string | null) => void;
  setDraftName: (name: string) => void;
  toggleMember: (userId: string) => void;
  startEditing: (group: DriverGroupDto) => void;
  cancelEditing: () => void;
  save: (onSaved: () => void) => void;
  forgetGroup: () => void;
}

export function useDriverGroupEditor(groups: DriverGroupDto[]): DriverGroupEditor {
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draftName, setDraftName] = useState<string>('');
  const [draftMemberIds, setDraftMemberIds] = useState<Set<string>>(new Set<string>());
  const updateMutation: ReturnType<typeof useUpdateDriverGroup> = useUpdateDriverGroup();
  const resetSaveError: () => void = updateMutation.reset;

  const activeGroup: DriverGroupDto | null =
    groups.find((group: DriverGroupDto): boolean => group.id === activeGroupId) ?? null;

  const selectGroup: (groupId: string | null) => void = useCallback(
    (groupId: string | null): void => {
      setActiveGroupId(groupId);
      setIsEditing(false);
    },
    []
  );

  const startEditing: (group: DriverGroupDto) => void = useCallback(
    (group: DriverGroupDto): void => {
      setActiveGroupId(group.id);
      setDraftName(group.name);
      setDraftMemberIds(new Set<string>(group.memberIds));
      setIsEditing(true);
      resetSaveError();
    },
    [resetSaveError]
  );

  const changeDraftName: (name: string) => void = useCallback(
    (name: string): void => {
      setDraftName(name);
      resetSaveError();
    },
    [resetSaveError]
  );

  const cancelEditing: () => void = useCallback((): void => {
    setIsEditing(false);
  }, []);

  const forgetGroup: () => void = useCallback((): void => {
    setActiveGroupId(null);
    setIsEditing(false);
  }, []);

  const toggleMember: (userId: string) => void = useCallback((userId: string): void => {
    setDraftMemberIds((previousMemberIds: Set<string>): Set<string> =>
      toggleMemberId(previousMemberIds, userId)
    );
  }, []);

  const isNameValid: boolean = draftName.trim().length > 0;

  const save = (onSaved: () => void): void => {
    saveDriverGroupDraft({
      activeGroup,
      draftName,
      draftMemberIds,
      updateDriverGroup: updateMutation.mutate,
      stopEditing: cancelEditing,
      onSaved,
    });
  };

  const isDirty: boolean = isDriverGroupDraftDirty({ activeGroup, draftName, draftMemberIds });

  return {
    activeGroupId,
    activeGroup,
    isEditing: isEditing && activeGroup !== null,
    draftName,
    draftMemberIds,
    isDirty,
    isNameValid,
    isSaving: updateMutation.isPending,
    saveError: updateMutation.error,
    selectGroup,
    setDraftName: changeDraftName,
    toggleMember,
    startEditing,
    cancelEditing,
    save,
    forgetGroup,
  };
}
